import Hook from "../../utils/hook"
import { fetchJson } from "../../utils/fetch"
import { decodeBase64Url } from "../../utils/base64"
import { credentialToJson } from "../../utils/credentials"

const TOTP_DIGIT_COUNT = 6

function totpDigitsFrom(value) {
  return value.replace(/\D/g, ``).slice(0, TOTP_DIGIT_COUNT)
}

function getPasskeyOptionsPublicKey(options) {
  const publicKey = options.publicKey
  publicKey.challenge = decodeBase64Url(publicKey.challenge)

  if (publicKey.allowCredentials?.length) {
    publicKey.allowCredentials = publicKey.allowCredentials.map(
      (credential) => ({
        ...credential,
        id: decodeBase64Url(credential.id),
      })
    )
  } else {
    delete publicKey.allowCredentials
  }

  return publicKey
}

class SignPagePasskeyAuthenticationError extends Error {}

// totp

export const SignPageTotpCodeInput = Hook(`SignPageTotpCodeInput`, {
  targets: [`code`, `digit`],
  actions: [
    {
      name: `digitInput`,
      targets: `digit`,
      type: `input`,
    },
    {
      name: `digitKeydown`,
      targets: `digit`,
      type: `keydown`,
    },
    {
      name: `digitPaste`,
      targets: `digit`,
      type: `paste`,
    },
  ],

  mounted() {
    this.sync()
    this.digitTargets[0]?.focus()
  },

  sync() {
    const code = this.digitTargets.map((i) => i.value).join(``)
    this.codeTarget.value = code
    this.codeTarget.dispatchEvent(new Event(`input`, { bubbles: true }))
    this.codeTarget.dispatchEvent(new Event(`change`, { bubbles: true }))
  },

  digitInputAction(e, target) {
    const index = this.getDigitTargetIndex(target)
    const value = totpDigitsFrom(e.target.value)

    if (value.length > 1) {
      this.fillFrom(value, index)
      return
    }

    e.target.value = value
    if (value && index < this.digitTargets.length - 1)
      this.digitTargets[index + 1].focus()

    this.sync()
  },

  digitKeydownAction(e, target) {
    const index = this.getDigitTargetIndex(target)
    if (e.key !== `Backspace`) return
    if (e.target.value || index === 0) return

    this.digitTargets[index - 1].focus()
    this.digitTargets[index - 1].value = ``
    this.sync()
  },

  digitPasteAction(e, target) {
    e.preventDefault()
    const index = this.getDigitTargetIndex(target)
    this.fillFrom(e.clipboardData.getData(`text`), index)
  },

  fillFrom(value, startIndex = 0) {
    totpDigitsFrom(value)
      .split(``)
      .forEach((digit, offset) => {
        const input = this.digitTargets[startIndex + offset]
        if (input) input.value = digit
      })

    const nextIndex = Math.min(
      startIndex + totpDigitsFrom(value).length,
      TOTP_DIGIT_COUNT - 1
    )
    this.digitTargets[nextIndex]?.focus()
    this.sync()
  },

  getDigitTargetIndex(target) {
    return this.digitTargets.indexOf(target)
  },
})

// passkey

export const SignPagePasskey = Hook(`SignPagePasskey`, {
  values: { optionsUrl: String, authenticateUrl: String, errorMessage: String },
  targets: [`button`],
  actions: [
    {
      name: `buttonClick`,
      targets: `button`,
      type: `click`,
    },
  ],
  abortController: null,

  mounted() {
    this.init({ prompt: false })
  },

  buttonClickAction() {
    this.init({ prompt: true })
  },

  async init({ prompt = false } = {}) {
    try {
      this.abortController?.abort()
      this.abortController = new AbortController()

      const optionsResponse = await fetchJson(this.optionsUrlValue)
      const options = await optionsResponse.json()
      const publicKey = getPasskeyOptionsPublicKey(options)

      const credential = await navigator.credentials.get({
        mediation: prompt ? `optional` : `conditional`,
        publicKey,
        signal: this.abortController.signal,
      })

      const authResponse = await fetchJson(
        this.authenticateUrlValue,
        {
          credential: credentialToJson(credential),
        },
        { allowErrorResponse: true }
      )

      if (!authResponse.ok && !authResponse.redirected)
        throw new SignPagePasskeyAuthenticationError(
          `Passkey authentication failed`
        )

      if (authResponse.redirected) {
        window.location = authResponse.url
        return
      }

      const authResult = await authResponse.json()
      window.location = authResult.redirect_to || `/`
    } catch (error) {
      if (error instanceof SignPagePasskeyAuthenticationError) {
        this.putFlash({
          type: `error`,
          text: this.errorMessageValue,
        })
      }

      // reinit non-prompt login if prompt one failed
      if (prompt) this.init({ prompt: false })
    }
  },
})
